import {getFormResultConfig} from "./formResultHelpers.js"

const copy = value => JSON.parse(JSON.stringify(value || {}))
const hasAnswer = value => value !== null && value !== undefined && value !== ""

export function getPairedQuestionnaireConfigErrors(config = {}) {
	if (!config.pairedQuestionnaire) return []
	const questionnaire = config.pairedQuestionnaire
	const errors = []
	const partners = questionnaire.partners || []
	const sections = questionnaire.sections || []
	const options = questionnaire.options || []
	const unique = rows => new Set(rows.map(row => row.id)).size === rows.length && rows.every(row => !!row.id)
	if (partners.length !== 2 || !unique(partners) || partners.some(row => !row.title)) errors.push("Two named partners are required")
	if (!unique(sections) || sections.some(row => !row.title || !Array.isArray(row.questions) || !unique(row.questions) || row.questions.some(question => !question.text))) errors.push("Sections and questions need unique IDs and text")
	const questions = sections.flatMap(section => section.questions || [])
	if (!unique(questions)) errors.push("Question IDs must be unique across sections")
	if (!options.length || !unique(options) || options.some(option => !option.title || !Number.isFinite(Number(option.score)))) errors.push("Response options need unique IDs, titles, and numeric scoring values")
	if (questionnaire.responseKind === "rating") {
		const scores = options.map(option => Number(option.score))
		const minimum = Math.min(...scores)
		const maximum = Math.max(...scores)
		if (![0, 1].includes(minimum) || maximum !== Number(questionnaire.ratingMax) || maximum < 3 || maximum > 10 || new Set(scores).size !== scores.length || !Array.from({length: maximum - minimum + 1}, (_, index) => minimum + index).every(score => scores.includes(score))) errors.push("Rating options must cover every whole number from zero or one through the selected maximum")
	} else if (questionnaire.responseKind && questionnaire.responseKind !== "choice") errors.push("Unknown response style")
	const scoring = questionnaire.scoring || {}
	if (!["sum", "none"].includes(scoring.method)) errors.push("Choose sum or descriptive scoring")
	if (scoring.method === "sum" && scoring.threshold !== null && scoring.threshold !== undefined && !Number.isFinite(Number(scoring.threshold))) errors.push("The score threshold must be numeric")
	return errors
}

export function getPairedQuestionnaireReport(config = {}, reports = {}) {
	const questionnaire = config.pairedQuestionnaire || {}
	const sections = questionnaire.sections || []
	const questions = sections.flatMap(section => section.questions || [])
	const optionMap = new Map((questionnaire.options || []).map(option => [option.id, option]))
	const maximumOptionScore = Math.max(0, ...(questionnaire.options || []).map(option => Number(option.score)))
	const partnerReports = (questionnaire.partners || []).map(partner => {
		const answers = reports[partner.id]?.answers || {}
		const sectionsView = sections.map(section => {
			const items = (section.questions || []).map(question => {
				const option = optionMap.get(answers[question.id])
				return {id: question.id, text: question.text, optionId: answers[question.id] ?? null, answer: option?.title || null, score: option && Number.isFinite(Number(option.score)) ? Number(option.score) : null, isAnswered: !!option}
			})
			const isComplete = items.every(item => item.isAnswered)
			return {id: section.id, title: section.title, explanation: section.explanation, items, answeredCount: items.filter(item => item.isAnswered).length, itemCount: items.length, isComplete, subtotal: isComplete && questionnaire.scoring?.method === "sum" ? items.reduce((sum, item) => sum + item.score, 0) : null, maximum: items.length * maximumOptionScore}
		})
		const answeredCount = sectionsView.reduce((sum, section) => sum + section.answeredCount, 0)
		const isComplete = answeredCount === questions.length && questions.length > 0
		const total = isComplete && questionnaire.scoring?.method === "sum" ? sectionsView.reduce((sum, section) => sum + section.subtotal, 0) : null
		const threshold = questionnaire.scoring?.threshold
		const interpretation = total === null || !Number.isFinite(Number(threshold)) || threshold === null || threshold === "" ? "" : total >= Number(threshold) ? questionnaire.scoring.atOrAboveText : questionnaire.scoring.belowText
		return {id: partner.id, title: reports[partner.id]?.name || partner.title, sections: sectionsView, answeredCount, itemCount: questions.length, isComplete, total, maximum: questions.length * maximumOptionScore, interpretation}
	})
	return {partners: partnerReports, isComparisonComplete: partnerReports.length === 2 && partnerReports.every(partner => partner.isComplete)}
}

function getPairedResultTime(result) {
	const value = result.createdAt
	const date = value?.toDate?.() || (value?.seconds !== undefined ? new Date(value.seconds * 1000) : new Date(value || 0))
	return Number.isNaN(date.getTime()) ? 0 : date.getTime()
}

function collectPairedResultEvidence(result, priorResults) {
	const sessionId = String(result.resultThreadId || "").split(":")[0]
	const candidates = [result, ...priorResults.filter(prior => sessionId &&
		String(prior.resultThreadId || "").split(":")[0] === sessionId && prior.toolType === result.toolType)]
	candidates.sort((a, b) => getPairedResultTime(b) - getPairedResultTime(a) || String(b.id || "").localeCompare(String(a.id || "")))
	const reports = {}
	const snapshots = {}
	for (const candidate of candidates) {
		for (const [partnerId, report] of Object.entries(candidate.enginePayload?.reports ?? candidate.summary?.reports ?? {})) {
			if (reports[partnerId]) continue
			reports[partnerId] = copy(report)
			snapshots[partnerId] = getFormResultConfig(undefined, candidate)
		}
	}
	return {reports, snapshots}
}

function getPairedRetainedAnswers(config, reports, snapshots) {
	const questionnaire = config.pairedQuestionnaire || {}
	const questions = new Map((questionnaire.sections || []).flatMap(section => section.questions || []).map(question => [question.id, question]))
	const options = new Map((questionnaire.options || []).map(option => [option.id, option]))
	return Object.entries(reports).map(([id, response]) => {
		const snapshot = snapshots[id]?.pairedQuestionnaire || {}
		const previousQuestions = new Map((snapshot.sections || []).flatMap(section => section.questions || []).map(question => [question.id, question]))
		const previousOptions = new Map((snapshot.options || []).map(option => [option.id, option]))
		const partner = (questionnaire.partners || []).find(partner => partner.id === id)
		const items = Object.entries(response.answers || {}).filter(([questionId, optionId]) => hasAnswer(optionId) &&
			(!partner || !questions.has(questionId) || !options.has(optionId))).map(([questionId, optionId]) => ({
			id: questionId, text: questions.get(questionId)?.text || previousQuestions.get(questionId)?.text || questionId,
			answer: options.get(optionId)?.title || previousOptions.get(optionId)?.title || String(optionId),
			isRemovedQuestion: !questions.has(questionId), isRemovedOption: !options.has(optionId),
		}))
		return {id, title: response.name || partner?.title || (snapshot.partners || []).find(partner => partner.id === id)?.title || id, items}
	}).filter(partner => partner.items.length)
}

export function getPairedQuestionnaireResultView(currentConfig, result = {}, priorResults = []) {
	const config = copy(getFormResultConfig(currentConfig, result))
	const {reports, snapshots} = collectPairedResultEvidence(result, priorResults)
	const report = getPairedQuestionnaireReport(config, reports)
	const [first, second] = report.partners
	const differences = report.isComparisonComplete ? first.sections.flatMap(section => section.items.map(item => {
		const other = second.sections.flatMap(row => row.items).find(row => row.id === item.id)
		return item.optionId !== other?.optionId ? {id: item.id, text: item.text, first: item.answer, second: other?.answer || "Unanswered"} : null
	}).filter(Boolean)) : []
	const retainedAnswers = getPairedRetainedAnswers(config, reports, snapshots)
	const questionIds = new Set((config.pairedQuestionnaire?.sections || []).flatMap(section => (section.questions || []).map(question => question.id)))
	const unmatchedAnswers = Object.fromEntries((config.pairedQuestionnaire?.partners || []).map(partner => [partner.id, Object.keys(reports[partner.id]?.answers || {}).filter(id => !questionIds.has(id))]))
	const optionIds = new Set((config.pairedQuestionnaire?.options || []).map(option => option.id))
	const removedOptionAnswers = Object.fromEntries((config.pairedQuestionnaire?.partners || []).map(partner => [partner.id, Object.entries(reports[partner.id]?.answers || {}).filter(([questionId, optionId]) => questionIds.has(questionId) && hasAnswer(optionId) && !optionIds.has(optionId)).map(([questionId]) => questionId)]))
	return {config, reports, report, differences, unmatchedAnswers, removedOptionAnswers, retainedAnswers}
}

export function getPairedQuestionnairePayloadErrors(payload = {}, config = {}) {
	if (!config.pairedQuestionnaire) return []
	const questionnaire = config.pairedQuestionnaire
	const partnerIds = new Set((questionnaire.partners || []).map(partner => partner.id))
	const questionIds = new Set((questionnaire.sections || []).flatMap(section => (section.questions || []).map(question => question.id)))
	const optionIds = new Set((questionnaire.options || []).map(option => option.id))
	const reports = payload.reports || {}
	const errors = []
	if (!Object.keys(reports).length) errors.push("At least one partner response is required")
	for (const [partnerId, report] of Object.entries(reports)) {
		if (!partnerIds.has(partnerId)) errors.push("Unknown partner response")
		for (const [questionId, optionId] of Object.entries(report?.answers || {})) {
			if (!questionIds.has(questionId)) continue
			if (hasAnswer(optionId) && !optionIds.has(optionId)) errors.push("Unknown response option")
		}
	}
	return errors
}

export function updatePairedQuestionnaireAnswer(payload = {}, config = {}, partnerId = "", questionId = "", optionId = null) {
	const questionnaire = config.pairedQuestionnaire || {}
	if (!(questionnaire.partners || []).some(partner => partner.id === partnerId)) throw new Error("Unknown partner")
	if (!(questionnaire.sections || []).some(section => (section.questions || []).some(question => question.id === questionId))) throw new Error("Unknown question")
	if (hasAnswer(optionId) && !(questionnaire.options || []).some(option => option.id === optionId)) throw new Error("Unknown option")
	const reports = copy(payload.reports)
	const answers = {...(reports[partnerId]?.answers || {})}
	if (hasAnswer(optionId)) answers[questionId] = optionId
	else delete answers[questionId]
	reports[partnerId] = {...reports[partnerId], answers}
	return {...payload, reports, form: copy(config)}
}

export function updatePairedQuestionnaireName(payload = {}, config = {}, partnerId = "", name = "") {
	if (!(config.pairedQuestionnaire?.partners || []).some(partner => partner.id === partnerId)) throw new Error("Unknown partner")
	const reports = copy(payload.reports)
	reports[partnerId] = {...reports[partnerId], name: String(name || ""), answers: reports[partnerId]?.answers || {}}
	return {...payload, reports, form: copy(config)}
}

const makeId = () => `question-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

export function markPairedQuestionnaireAdapted(config = {}) {
	const next = copy(config)
	const scoring = next.pairedQuestionnaire?.scoring
	if (scoring) {
		scoring.threshold = null
		scoring.atOrAboveText = ""
		scoring.belowText = ""
	}
	return next
}

function moveRow(rows, index, offset) {
	const target = index + offset
	if (index >= 0 && index < rows.length && target >= 0 && target < rows.length) rows.splice(target, 0, rows.splice(index, 1)[0])
}

function renumberRatingOptions(questionnaire, minimum) {
	questionnaire.options.forEach((option, index) => {
		const score = minimum + index
		if (option.title === String(option.score)) option.title = String(score)
		option.score = score
	})
	questionnaire.ratingMax = minimum + questionnaire.options.length - 1
}

export function addPairedQuestionnaireSection(config = {}) {
	const next = copy(config)
	next.pairedQuestionnaire.sections.push({id: makeId(), title: "New section", explanation: "", questions: []})
	return markPairedQuestionnaireAdapted(next)
}

export function removePairedQuestionnaireSection(config = {}, sectionId = "") {
	const next = copy(config)
	next.pairedQuestionnaire.sections = next.pairedQuestionnaire.sections.filter(section => section.id !== sectionId)
	return markPairedQuestionnaireAdapted(next)
}

export function movePairedQuestionnaireSection(config = {}, index = 0, offset = 0) {
	const next = copy(config)
	moveRow(next.pairedQuestionnaire.sections, index, offset)
	return markPairedQuestionnaireAdapted(next)
}

export function addPairedQuestionnaireQuestion(config = {}, sectionId = "") {
	const next = copy(config)
	const section = next.pairedQuestionnaire.sections.find(row => row.id === sectionId)
	if (section) section.questions.push({id: makeId(), text: "New question"})
	return markPairedQuestionnaireAdapted(next)
}

export function removePairedQuestionnaireQuestion(config = {}, sectionId = "", questionId = "") {
	const next = copy(config)
	const section = next.pairedQuestionnaire.sections.find(row => row.id === sectionId)
	if (section) section.questions = section.questions.filter(question => question.id !== questionId)
	return markPairedQuestionnaireAdapted(next)
}

export function movePairedQuestionnaireQuestion(config = {}, sectionId = "", index = 0, offset = 0) {
	const next = copy(config)
	const section = next.pairedQuestionnaire.sections.find(row => row.id === sectionId)
	if (section) moveRow(section.questions, index, offset)
	return markPairedQuestionnaireAdapted(next)
}

export function movePairedQuestionnaireQuestionToSection(config = {}, sectionId = "", questionId = "", destinationId = "") {
	const next = copy(config)
	const source = next.pairedQuestionnaire.sections.find(row => row.id === sectionId)
	const destination = next.pairedQuestionnaire.sections.find(row => row.id === destinationId)
	if (source && destination && source !== destination) {
		const index = source.questions.findIndex(question => question.id === questionId)
		if (index >= 0) destination.questions.push(source.questions.splice(index, 1)[0])
	}
	return markPairedQuestionnaireAdapted(next)
}

export function addPairedQuestionnaireOption(config = {}) {
	const next = copy(config)
	const questionnaire = next.pairedQuestionnaire
	if (questionnaire.responseKind === "rating") {
		const score = Math.max(...questionnaire.options.map(option => Number(option.score))) + 1
		if (score <= 10) {
			questionnaire.options.push({id: `rating-${score}`, title: String(score), score})
			questionnaire.ratingMax = score
		}
	} else questionnaire.options.push({id: makeId(), title: "New option", score: 0})
	return markPairedQuestionnaireAdapted(next)
}

export function removePairedQuestionnaireOption(config = {}, optionId = "") {
	const next = copy(config)
	const questionnaire = next.pairedQuestionnaire
	const removed = questionnaire.options.find(option => option.id === optionId)
	questionnaire.options = questionnaire.options.filter(option => option.id !== optionId)
	if (questionnaire.responseKind === "rating" && questionnaire.options.length) {
		const minimum = Number(removed?.score) === 0 ? 1 : Math.min(...questionnaire.options.map(option => Number(option.score)))
		renumberRatingOptions(questionnaire, minimum)
		if (questionnaire.ratingMax < 3) questionnaire.responseKind = "choice"
	}
	return markPairedQuestionnaireAdapted(next)
}

export function movePairedQuestionnaireOption(config = {}, index = 0, offset = 0) {
	const next = copy(config)
	const questionnaire = next.pairedQuestionnaire
	const minimum = Math.min(...questionnaire.options.map(option => Number(option.score)))
	moveRow(questionnaire.options, index, offset)
	if (questionnaire.responseKind === "rating") renumberRatingOptions(questionnaire, minimum)
	return markPairedQuestionnaireAdapted(next)
}

export function setPairedQuestionnaireRatingMaximum(config = {}, maximum = 5) {
	const next = copy(config)
	const questionnaire = next.pairedQuestionnaire
	const previous = new Map(questionnaire.options.map(option => [Number(option.score), option]))
	const minimum = questionnaire.options.length > 2 && Math.min(...previous.keys()) === 1 ? 1 : 0
	questionnaire.ratingMax = Number(maximum)
	questionnaire.options = Array.from({length: questionnaire.ratingMax - minimum + 1}, (_, index) => {
		const score = minimum + index
		return previous.get(score)?.id?.startsWith("rating-") ? previous.get(score) : {id: `rating-${score}`, title: String(score), score}
	})
	return markPairedQuestionnaireAdapted(next)
}

export function setPairedQuestionnaireResponseKind(config = {}, kind = "choice") {
	const next = copy(config)
	const questionnaire = next.pairedQuestionnaire
	questionnaire.responseKind = kind
	questionnaire.instructions = kind === "rating" ? "Read each statement and rate how true it feels from your viewpoint about your partner." : "Read each statement and choose the response that best reflects your viewpoint about your partner."
	return kind === "rating" ? setPairedQuestionnaireRatingMaximum(next, questionnaire.ratingMax || 5) : markPairedQuestionnaireAdapted(next)
}
